<template>
    <div class="under-header p-10" :style="backgroundImageStyle" key="page-anim">
        <div class="mobile-background">
            <h1 class="font-medium layoutHeader blur">{{ page?.altTitle || page?.title || 'Contact' }}</h1>
            <div class="layoutHeader blur">
                <p class="mt-0 pt-4">Together we can unlock innovation and enrich lives.</p>
                <p>Contact us directly or via the enquiry form below.</p>
                <p v-if="isOnline"><a href="tel:+31546545151">T: +31 (0)546 545 151</a></p>
                <p v-else>T: +31 (0)546 545 151</p>
                <p v-if="isOnline" class="mb-0 pb-4"><a href="mailto:isotopes@urenco.com">isotopes@urenco.com</a></p>
                <p v-else class="mb-0 pb-4">isotopes@urenco.com</p>
            </div>
            <div class="contactSplitter">
                <div class="contactSplitterPanel">
                    <form @submit.prevent="onSubmit" class="grid pt-4">
                        <div class="col-12 sm:col-6">
                            <label for="name" class="hidden mb-1">Name *</label>
                            <InputText id="name" placeholder="Name *" v-model="form.name" required class="w-full" />
                        </div>
                        <div class="col-12 sm:col-6">
                            <label for="phone" class="hidden mb-1">Phone number *</label>
                            <InputText id="phone" placeholder="Phone number *" v-model="form.phone" type="phone" required class="w-full" />
                        </div>
                        <div class="col-12 sm:col-6">
                            <label for="email" class="hidden mb-1">Email address *</label>
                            <InputText id="email" placeholder="Email address *" v-model="form.email" type="email" required class="w-full" />
                        </div>
                        <div class="col-12 sm:col-6">
                            <label for="post_address" class="hidden mb-1">Post address *</label>
                            <InputText id="post_address" placeholder="Post address *" v-model="form.post_address" required class="w-full" />
                        </div>
                        <div class="col-12">
                            <label for="enquiry" class="hidden mb-1">Enquiry *</label>
                            <Textarea id="enquiry" rows="5" cols="30" style="resize: none" placeholder="Enquiry" v-model="form.enquiry" required class="w-full" />
                        </div>
                        <input type="hidden" v-model="form.referral_url" />
                        <input type="hidden" v-model="form.recaptcha" />
                        <div class="col-12">
                            <Button type="submit" label="Submit" class="w-full" :disabled="loading" />
                        </div>
                        <div class="col-12 flex align-items-top">
                            <Checkbox id="marketing_source" v-model="form.marketing_source" :binary="true" />
                            <label for="marketing_source" class="ml-2">By submitting this form, you agree to allow Urenco Ltd to store and process your contact details. Tick this box if you would like to opt in to receive marketing content. View our <a href="https://urenco.com/privacy-policy" class="font-black" target="_blank" title="Open Global Urenco Privacy Policy" @click="openPrivacyPolicy">privacy policy</a>.</label>
                        </div>
                    </form>
                </div>
                <div class="spinning-background-1"></div>
            </div>
        </div>
    </div>
    
    <!-- Toast for notifications -->
    <Toast position="top-right" />

    <!-- Privacy Policy Modal -->
    <Dialog v-model:visible="showPrivacyModal" modal header="Privacy Policy" :style="{ width: '25rem' }" class="privacy-modal">
        <div class="flex flex-column align-items-center p-4">
            <p class="text-center mb-3">Scan the QR code to view:</p>
            <img :src="displayPrivacyPolicyQrCode" alt="QR code for privacy policy" class="w-16rem h-16rem border-1 border-300 p-2 bg-white" />
            <p class="text-center mt-3 text-sm text-500">https://www.urenco.com/privacy-policy</p>
        </div>
        <template #footer>
            <Button label="Close" @click="showPrivacyModal = false" class="w-full" />
        </template>
    </Dialog>
</template>
<script setup>
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';
import Toast from 'primevue/toast';
import Dialog from 'primevue/dialog';
import { computed, onUnmounted, reactive, ref } from 'vue';
import { useCommonStyles } from '../composables/useCommonStyles.js';
import { isConnected } from '../composables/useConnectivityStatus';
import { useToast } from '../composables/useToast.js';
import OfflineSubmissionService from '../services/OfflineSubmissionService.js';

const props = defineProps({
  page: Object,
  settings: Object
});

const loading = ref(false);
const isOnline = computed(() => isConnected.value);
const showPrivacyModal = ref(false);
const displayPrivacyPolicyQrCode = ref('/images/privacy-qr.png');
const { showSuccess, showError } = useToast();
const form = reactive({
    name: '',
    phone: '',
    email: '',
    post_address: '',
    enquiry: '',
    referral_url: window.location.href, // autopopulate with current URL
    marketing_source: false,
    recaptcha: ''
});

// Use common styles composable
const { backgroundImageStyle } = useCommonStyles(props);

const privacyPolicyQrCode = computed(() => {
  return '/images/privacy-qr.png';
});

let privacyQrBlobUrl = null;

const revokePrivacyQrBlobUrl = () => {
    if (!privacyQrBlobUrl) {
        return;
    }

    URL.revokeObjectURL(privacyQrBlobUrl);
    privacyQrBlobUrl = null;
};

const resolvePrivacyPolicyQrCode = async () => {
    revokePrivacyQrBlobUrl();

    if (isConnected.value || !('caches' in window)) {
        displayPrivacyPolicyQrCode.value = privacyPolicyQrCode.value;
        return;
    }

    try {
        const cache = await caches.open('app-cache-v1-images');
        const cachedResponse = await cache.match(privacyPolicyQrCode.value);

        if (cachedResponse) {
            const blob = await cachedResponse.blob();
            privacyQrBlobUrl = URL.createObjectURL(blob);
            displayPrivacyPolicyQrCode.value = privacyQrBlobUrl;
            return;
        }
    } catch {
        // ignore cache lookup errors and fallback to original src
    }

    displayPrivacyPolicyQrCode.value = privacyPolicyQrCode.value;
};

const openPrivacyPolicy = async (event) => {
    if (isOnline.value) {
        return;
    }

    event.preventDefault();
    await resolvePrivacyPolicyQrCode();
    showPrivacyModal.value = true;
};

onUnmounted(() => {
    revokePrivacyQrBlobUrl();
});

const onSubmit = async () => {
    loading.value = true;
    try {
        const recaptchaClient = window.grecaptcha;
        if (isConnected.value && recaptchaClient?.ready && recaptchaClient?.execute) {
            const siteKey = window.AppConfig.recaptchaSiteKey;
            await new Promise((resolve) => recaptchaClient.ready(resolve));
            form.recaptcha = await recaptchaClient.execute(siteKey, { action: 'submit' });
        } else {
            form.recaptcha = '';
        }
        // Send form with token as 'g-recaptcha-response'
        const payload = {
        ...form,
        'g-recaptcha-response': form.recaptcha
        };
        const result = await OfflineSubmissionService.submitWithOfflineSupport('/api/lead-submissions', payload);
        
        if (result.status === 'queued') {
            showSuccess('Saved offline. Will sync automatically when connected.');
        } else {
            showSuccess("Thank you! We've received your request and will get back to you as soon as possible. If you need immediate assistance, feel free to contact us directly via isotopes@urenco.com or +31 (0)546 545 151.");
        }
        
        // Reset form after successful submission
        Object.keys(form).forEach(key => {
            if (key === 'referral_url') {
                form[key] = window.location.href; // Keep current URL
            } else if (key === 'marketing_source') {
                form[key] = false; // Reset checkbox
            } else {
                form[key] = ''; // Reset text fields
            }
        });
        
    } catch (error) {
        console.error(error);
        const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Submission failed. Please try again.';
        showError(errorMessage);
    } finally {
        loading.value = false;
    }
};
</script>